import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Eye,
  FileText,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { OSS_PERSONALE_DOC_TYPES } from "../../config/ossPersonaleDocumenti";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("it-IT");
}

export default function StaffDocumentiDialog({
  open,
  onOpenChange,
  staff,
  token,
  canEdit = false,
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeType, setActiveType] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const staffId = staff?.id;
  const staffName = staff
    ? `${staff.nome || ""} ${staff.cognome || ""}`.trim()
    : "";

  const fetchDocs = useCallback(async () => {
    if (!token || !staffId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/oss/${staffId}/documenti`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(data.documents || []);
    } catch {
      toast.error("Errore caricamento documenti");
    } finally {
      setLoading(false);
    }
  }, [token, staffId]);

  useEffect(() => {
    if (open && staffId) {
      setActiveType(null);
      fetchDocs();
    }
  }, [open, staffId, fetchDocs]);

  const docsForType = (type) => documents.filter((d) => d.doc_type === type);
  const activeMeta = OSS_PERSONALE_DOC_TYPES.find((t) => t.key === activeType);
  const modalDocs = activeType ? docsForType(activeType) : [];

  const fetchBlob = async (fileId) => {
    const { data } = await axios.get(
      `${API_URL}/api/oss/${staffId}/documenti/${fileId}/file`,
      { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
    );
    return data;
  };

  const handlePreview = async (doc) => {
    try {
      const blob = await fetchBlob(doc.file_id);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      toast.error("Impossibile aprire il documento");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const blob = await fetchBlob(doc.file_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || "documento";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossibile scaricare il documento");
    }
  };

  const handleDelete = async (doc) => {
    if (!canEdit) return;
    if (!window.confirm(`Eliminare "${doc.file_name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/oss/${staffId}/documenti/${doc.file_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Documento eliminato");
      await fetchDocs();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeType || !canEdit) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File troppo grande (max 50 MB)");
      e.target.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("descrizione", file.name || "");
    try {
      await axios.post(`${API_URL}/api/oss/${staffId}/documenti/${activeType}`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Documento caricato");
      await fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) setActiveType(null);
          onOpenChange(next);
        }}
      >
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {activeType
                ? activeMeta?.label || "Documenti"
                : `Documenti — ${staffName || "Operatore"}`}
            </DialogTitle>
            <DialogDescription>
              {activeType
                ? `${staffName} — ${activeMeta?.description || ""}`
                : "Attestato OSS, documenti, corsi, BLSD, HACCP e certificati. L'operatore può caricarli anche dall'area OSS."}
            </DialogDescription>
          </DialogHeader>

          {activeType && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-fit -mt-1 mb-1"
              onClick={() => setActiveType(null)}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Tutte le categorie
            </Button>
          )}

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
            </div>
          ) : !activeType ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {OSS_PERSONALE_DOC_TYPES.map(({ key, label, description, Icon, iconWrap }) => {
                const n = docsForType(key).length;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveType(key)}
                    className="text-left rounded-xl border border-slate-100 bg-white p-3 hover:border-sky-200 hover:bg-sky-50/40 transition-colors"
                  >
                    <div className="flex items-start gap-2.5">
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center ring-1 shrink-0 ${iconWrap}`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-1">
                          <p className="text-sm font-semibold text-slate-800">{label}</p>
                          <span
                            className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full ${
                              n > 0
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {n}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {canEdit && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.heic,.heif,.doc,.docx,.xls,.xlsx"
                    onChange={handleUpload}
                  />
                  <Button
                    type="button"
                    className="w-full bg-sky-600 hover:bg-sky-700"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Carica file
                  </Button>
                </>
              )}

              {modalDocs.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                  <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Nessun documento in questa categoria</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {modalDocs.map((doc) => (
                    <li
                      key={doc.file_id}
                      className="flex items-center gap-2 rounded-xl border border-slate-100 p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{doc.file_name}</p>
                        <p className="text-xs text-slate-500">
                          {formatDate(doc.uploaded_at)}
                          {doc.uploaded_by === "oss"
                            ? " · caricato da OSS"
                            : doc.uploaded_by_name
                              ? ` · ${doc.uploaded_by_name}`
                              : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handlePreview(doc)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDownload(doc)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {canEdit && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(doc)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!previewUrl}
        onOpenChange={(openPrev) => {
          if (!openPrev && previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Anteprima</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <iframe title="Anteprima documento" src={previewUrl} className="flex-1 w-full rounded-lg border" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
